import React from "react";
import { Link } from "react-router-dom";
import { FileText, FolderOpen, Database, Layers, Image as ImageIcon, Ban, Server, Clock, Check, ArrowRight } from "lucide-react";
import { site } from "@/config/site";

const WhatsAppGlyph = () => null;

export default function HowItWorks() {
  return (
    <>
      {/* HERO */}
      <section
        className="text-white"
        style={{ background: "linear-gradient(105deg,#0F2B46 0%,#196CA3 100%)" }}
      >
        <div className="mx-auto max-w-site px-5 lg:px-8 py-16 lg:py-20">
          <div className="rule" style={{ background: "#4FC3F7" }} />
          <div className="eyebrow !text-brand-light">How it works</div>
          <h1 className="mt-3 text-4xl sm:text-5xl font-extrabold leading-tight tracking-tight max-w-3xl">
            Three steps. Then never think about it again.
          </h1>
        </div>
      </section>

      {/* THREE STEPS DETAILED */}
      <section className="bg-white">
        <div className="mx-auto max-w-site px-5 lg:px-8 py-20 space-y-14">
          {[
            {
              n: 1,
              title: "We install it.",
              body:
                "A small, quiet program on each computer. Our team does it — remotely over screen-share or in-person at your office. It takes about 15 minutes per machine, once. After that, you don't touch it again.",
            },
            {
              n: 2,
              title: "It backs up daily, by itself.",
              body:
                "Every night, the program checks which files have changed and copies them to secure cloud storage. No buttons to press. No reminders. If a computer is off or offline, it picks up where it left off the next time it's on.",
            },
            {
              n: 3,
              title: "You get your files back.",
              body:
                "Deleted a file by accident? Machine died? Ransomware locked everything? You message us. We pull the version you need from your backup and get it back on your computer — usually the same day.",
            },
          ].map((s) => (
            <div key={s.n} className="grid grid-cols-1 md:grid-cols-[64px_1fr] gap-6">
              <div className="h-16 w-16 rounded-full bg-brand text-white flex items-center justify-center font-extrabold text-2xl">
                {s.n}
              </div>
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-ink leading-tight">
                  {s.title}
                </h2>
                <p className="mt-3 text-ink-body text-lg leading-relaxed max-w-3xl">{s.body}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* WHAT GETS BACKED UP / SKIPPED */}
      <section style={{ backgroundColor: "#F8FAFC" }}>
        <div className="mx-auto max-w-site px-5 lg:px-8 py-20">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
            <div>
              <div className="rule" />
              <div className="eyebrow">What gets backed up</div>
              <h2 className="mt-2 text-2xl md:text-3xl font-bold text-ink">
                The files that run your business.
              </h2>
              <ul className="mt-6 space-y-3">
                {[
                  { icon: FileText, t: "Your documents, spreadsheets, PDFs" },
                  { icon: Database, t: "Tally / accounting data folders" },
                  { icon: Layers, t: "Design, CAD, and drawing files" },
                  { icon: ImageIcon, t: "Scanned records and photos" },
                  { icon: FolderOpen, t: "Any folder you tell us matters" },
                ].map((r, i) => (
                  <li key={i} className="flex items-center gap-3 text-ink-body">
                    <span className="h-10 w-10 rounded-md bg-brand-card text-brand flex items-center justify-center shrink-0">
                      <r.icon className="h-5 w-5" strokeWidth={1.75} />
                    </span>
                    {r.t}
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <div className="rule" />
              <div className="eyebrow">What we skip</div>
              <h2 className="mt-2 text-2xl md:text-3xl font-bold text-ink">
                Things we don't waste space on.
              </h2>
              <ul className="mt-6 space-y-3">
                {[
                  "Windows system files and program installations",
                  "Temporary files and cache",
                  "Downloads folder (optional — your call)",
                ].map((t, i) => (
                  <li key={i} className="flex items-center gap-3 text-ink-body">
                    <span className="h-10 w-10 rounded-md bg-hairline text-ink-body flex items-center justify-center shrink-0">
                      <Ban className="h-5 w-5" strokeWidth={1.75} />
                    </span>
                    {t}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* RESTORE FLOW */}
      <section className="bg-white">
        <div className="mx-auto max-w-site px-5 lg:px-8 py-20 max-w-4xl">
          <div className="rule" />
          <div className="eyebrow">Restoring your files</div>
          <h2 className="mt-2 text-2xl md:text-3xl font-bold text-ink">
            How restore works.
          </h2>
          <ol className="mt-8 space-y-5">
            {[
              { n: 1, body: 'You message us: "I need the Tally folder from last Tuesday."' },
              { n: 2, body: "We pull that version from your backup." },
              { n: 3, body: "You get the files back — usually the same day." },
            ].map((s) => (
              <li key={s.n} className="flex gap-4 items-start">
                <div className="h-10 w-10 rounded-full bg-brand text-white font-bold text-base flex items-center justify-center shrink-0">
                  {s.n}
                </div>
                <p className="text-ink-body text-lg leading-relaxed pt-1">{s.body}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* WHERE + HOW LONG */}
      <section style={{ backgroundColor: "#F8FAFC" }}>
        <div className="mx-auto max-w-site px-5 lg:px-8 py-20 grid grid-cols-1 md:grid-cols-2 gap-10">
          <div className="rounded-card bg-white p-8">
            <div className="inline-flex items-center justify-center h-11 w-11 rounded-md bg-brand-card text-brand">
              <Server className="h-5 w-5" strokeWidth={1.75} />
            </div>
            <h3 className="mt-4 text-xl font-bold text-ink">Where your data lives</h3>
            <p className="mt-3 text-ink-body leading-relaxed">
              Encrypted and stored on ISO 27001-certified enterprise cloud infrastructure,
              across multiple geographically-redundant data centres. Only you and VaultSafe
              can access it.
            </p>
          </div>
          <div className="rounded-card bg-white p-8">
            <div className="inline-flex items-center justify-center h-11 w-11 rounded-md bg-brand-card text-brand">
              <Clock className="h-5 w-5" strokeWidth={1.75} />
            </div>
            <h3 className="mt-4 text-xl font-bold text-ink">How long setup takes</h3>
            <p className="mt-3 text-ink-body leading-relaxed">
              15 minutes per computer. We do it — remotely or in person. You keep working
              while it runs.
            </p>
          </div>
        </div>
      </section>

      {/* GREEN CTA BAND */}
      <section style={{ background: "linear-gradient(100deg,#1D6C35 0%,#2C994D 100%)" }}>
        <div className="mx-auto max-w-site px-5 lg:px-8 py-16 lg:py-20">
          <div className="grid grid-cols-1 md:grid-cols-[2fr_1fr] gap-8 items-center">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-white leading-tight max-w-2xl">
                Ready to get set up?
              </h2>
              <p className="mt-3 text-white/85 max-w-xl">
                Send us a message. We'll schedule installation this week.
              </p>
            </div>
            <div className="flex md:justify-end">
              <Link
                to="/get-started"
                className="inline-flex items-center gap-2 rounded-md bg-white text-leaf-dark px-6 py-3.5 font-bold text-base shadow-card-lift hover:-translate-y-px transition-all"
                data-testid="how-it-works-cta"
              >
                Schedule Installation
                <ArrowRight className="h-4 w-4" strokeWidth={2.4} />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
